import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layouts/ClientLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Users, User, Loader2, Crown, Plus, Search
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AttendeeMessages = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('group');
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [groupChat, setGroupChat] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [organizer, setOrganizer] = useState(null);
  const [newMsgDialogOpen, setNewMsgDialogOpen] = useState(false);
  const [batchmates, setBatchmates] = useState([]);
  const [batchmateSearch, setBatchmateSearch] = useState('');
  const [loadingBatchmates, setLoadingBatchmates] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  const eventId = user?.event_id;

  useEffect(() => {
    if (eventId) {
      fetchGroupChat();
      fetchConversations();
      fetchOrganizer();
      setupWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [eventId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, groupMessages]);

  const setupWebSocket = () => {
    if (!token) return;
    
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    wsRef.current = new WebSocket(`${wsUrl}/api/ws/messages/${token}`);
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_message' && data.conversation_id === selectedConversation?.id) {
        setMessages(prev => [...prev, data.message]);
      } else if (data.type === 'group_message' && data.event_id === eventId) {
        setGroupMessages(prev => [...prev, data.message]);
      }
    };
    
    wsRef.current.onclose = () => {
      setTimeout(setupWebSocket, 3000);
    };
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchOrganizer = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.organizer) {
        setOrganizer(response.data.organizer);
      }
    } catch (error) {
      console.error('Failed to fetch event details:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupChat = async () => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API_URL}/api/messages/group/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupChat(response.data);
      fetchGroupMessages();
    } catch (error) {
      console.error('Failed to fetch group chat:', error);
    }
  };

  const fetchGroupMessages = async () => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API_URL}/api/messages/group/${eventId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch group messages:', error);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    try {
      const response = await axios.get(`${API_URL}/api/messages/conversations/${conv.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const startConversationWithOrganizer = async () => {
    if (!organizer) return;
    try {
      const response = await axios.post(`${API_URL}/api/messages/conversations`, 
        { participant_id: organizer.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedConversation({ ...response.data, other_user: organizer });
      setMessages([]);
      fetchConversations();
      setActiveTab('direct');
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const openNewMessageDialog = async () => {
    setNewMsgDialogOpen(true);
    setBatchmateSearch('');
    if (batchmates.length === 0) {
      setLoadingBatchmates(true);
      try {
        const response = await axios.get(`${API_URL}/api/event/attendees?event_id=${eventId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBatchmates(response.data.filter(a => a.id !== user?.id));
      } catch (error) {
        toast.error('Failed to load batchmates');
      } finally {
        setLoadingBatchmates(false);
      }
    }
  };

  const startConversationWith = async (attendee) => {
    try {
      const response = await axios.post(`${API_URL}/api/messages/conversations`,
        { participant_id: attendee.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedConversation({ ...response.data, other_user: attendee });
      setMessages([]);
      fetchConversations();
      setNewMsgDialogOpen(false);
      setActiveTab('direct');
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const filteredBatchmates = batchmates.filter(b => {
    const search = batchmateSearch.toLowerCase();
    const name = `${b.first_name} ${b.last_name}`.toLowerCase();
    return name.includes(search) || (b.email || '').toLowerCase().includes(search);
  });

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      if (activeTab === 'direct' && selectedConversation) {
        const response = await axios.post(
          `${API_URL}/api/messages/conversations/${selectedConversation.id}`,
          { content: newMessage },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessages(prev => [...prev, response.data]);
      } else if (activeTab === 'group' && eventId) {
        const response = await axios.post(
          `${API_URL}/api/messages/group/${eventId}`,
          { content: newMessage },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setGroupMessages(prev => [...prev, response.data]);
      }
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <ClientLayout>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="h-8 w-8 text-maroon" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
            <p className="text-gray-600">Stay connected with your reunion</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="group" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Group Chat
            </TabsTrigger>
            <TabsTrigger value="direct" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Direct Messages
            </TabsTrigger>
          </TabsList>

          {/* Group Chat Tab */}
          <TabsContent value="group">
            <Card className="h-[550px] flex flex-col">
              <CardHeader className="pb-2 border-b">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-maroon/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-maroon" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {groupChat?.name || 'Reunion Group Chat'}
                    </CardTitle>
                    <p className="text-sm text-gray-500">Chat with all attendees</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-[370px] p-4">
                  {groupMessages.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    groupMessages.map((msg, idx) => (
                      <div key={msg.id || idx} className="mb-4">
                        <div className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] ${
                            msg.sender_id === user?.id 
                              ? 'bg-maroon text-white' 
                              : 'bg-gray-100 text-gray-900'
                          } rounded-lg p-3`}>
                            {msg.sender_id !== user?.id && (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium">
                                  {msg.sender_name}
                                </span>
                                {msg.sender_role === 'organizer' && (
                                  <Badge variant="outline" className="text-xs bg-gold/20 border-gold text-gold-dark">
                                    <Crown className="h-3 w-3 mr-1" />
                                    Organizer
                                  </Badge>
                                )}
                              </div>
                            )}
                            <p>{msg.content}</p>
                            <p className={`text-xs mt-1 ${
                              msg.sender_id === user?.id ? 'text-white/70' : 'text-gray-500'
                            }`}>
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </ScrollArea>
              </CardContent>
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type a message to the group..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    className="flex-1"
                  />
                  <Button onClick={sendMessage} disabled={sending} className="bg-maroon hover:bg-maroon/90">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Direct Messages Tab */}
          <TabsContent value="direct">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[550px]">
              {/* Conversations List */}
              <Card className="lg:col-span-1 flex flex-col">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Conversations</CardTitle>
                    <Button 
                      size="sm" 
                      className="bg-maroon hover:bg-maroon-hover text-white"
                      onClick={openNewMessageDialog}
                      data-testid="new-message-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      New
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden p-2">
                  <ScrollArea className="h-full">
                    {/* Message Organizer Button */}
                    {organizer && (
                      <div
                        onClick={startConversationWithOrganizer}
                        className="p-3 rounded-lg cursor-pointer mb-2 bg-gold/10 border border-gold hover:bg-gold/20 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={organizer.profile_photo} />
                            <AvatarFallback className="bg-gold text-white">
                              {organizer.first_name?.[0]}{organizer.last_name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">
                                {organizer.first_name} {organizer.last_name}
                              </p>
                              <Crown className="h-4 w-4 text-gold" />
                            </div>
                            <p className="text-sm text-gray-500">Event Organizer</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Existing Conversations */}
                    {conversations.map(conv => (
                      <div
                        key={conv.id}
                        onClick={() => selectConversation(conv)}
                        className={`p-3 rounded-lg cursor-pointer mb-2 transition-colors ${
                          selectedConversation?.id === conv.id 
                            ? 'bg-maroon/10 border border-maroon' 
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={conv.other_user?.profile_photo} />
                            <AvatarFallback>
                              {conv.other_user?.first_name?.[0]}{conv.other_user?.last_name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {conv.other_user?.first_name} {conv.other_user?.last_name}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              {conv.last_message_preview || 'No messages yet'}
                            </p>
                          </div>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-maroon">{conv.unread_count}</Badge>
                          )}
                        </div>
                      </div>
                    ))}

                    {conversations.length === 0 && !organizer && (
                      <div className="text-center text-gray-500 py-8">
                        <p>No conversations yet</p>
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Messages Area */}
              <Card className="lg:col-span-2 flex flex-col">
                {selectedConversation ? (
                  <>
                    <CardHeader className="pb-2 border-b">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={selectedConversation.other_user?.profile_photo} />
                          <AvatarFallback>
                            {selectedConversation.other_user?.first_name?.[0]}
                            {selectedConversation.other_user?.last_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {selectedConversation.other_user?.first_name} {selectedConversation.other_user?.last_name}
                            {selectedConversation.other_user?.role === 'organizer' && (
                              <Crown className="h-4 w-4 text-gold" />
                            )}
                          </CardTitle>
                          <p className="text-sm text-gray-500">{selectedConversation.other_user?.email}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1 overflow-hidden p-0">
                      <ScrollArea className="h-[350px] p-4">
                        {messages.map((msg, idx) => (
                          <div
                            key={msg.id || idx}
                            className={`mb-4 flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[70%] ${
                              msg.sender_id === user?.id 
                                ? 'bg-maroon text-white' 
                                : 'bg-gray-100 text-gray-900'
                            } rounded-lg p-3`}>
                              <p>{msg.content}</p>
                              <p className={`text-xs mt-1 ${
                                msg.sender_id === user?.id ? 'text-white/70' : 'text-gray-500'
                              }`}>
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </ScrollArea>
                    </CardContent>
                    <div className="p-4 border-t">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                          className="flex-1"
                        />
                        <Button onClick={sendMessage} disabled={sending} className="bg-maroon hover:bg-maroon/90">
                          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Select a conversation to start messaging</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* New Message Dialog */}
        <Dialog open={newMsgDialogOpen} onOpenChange={setNewMsgDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-maroon">New Message</DialogTitle>
            </DialogHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search batchmates..."
                value={batchmateSearch}
                onChange={(e) => setBatchmateSearch(e.target.value)}
                className="pl-9"
                data-testid="search-batchmates-input"
              />
            </div>
            <ScrollArea className="h-72">
              {loadingBatchmates ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-maroon" />
                </div>
              ) : filteredBatchmates.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No batchmates found</p>
              ) : (
                filteredBatchmates.map(mate => (
                  <div
                    key={mate.id}
                    onClick={() => startConversationWith(mate)}
                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    data-testid={`batchmate-${mate.id}`}
                  >
                    <Avatar>
                      <AvatarImage src={mate.profile_image_file_id ? `${API_URL}/api/files/download/${mate.profile_image_file_id}` : mate.profile_photo} />
                      <AvatarFallback className="bg-gold/20 text-maroon font-medium">
                        {mate.first_name?.[0]}{mate.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-gray-900">{mate.first_name} {mate.last_name}</p>
                      <p className="text-sm text-gray-500">{mate.batch_year ? `Batch ${mate.batch_year}` : mate.email}</p>
                    </div>
                  </div>
                ))
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    </ClientLayout>
  );
};

export default AttendeeMessages;
